import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useListAllowListMembers } from 'src/features/parkingManagement/api/AllowListAPI/useListAllowListMembers';
import CreateAllowListMemberFormModal from 'src/features/parkingManagement/components/AllowLists/forms/CreateAllowListMemberFormModal';
import CellAllowListMemberActions from 'src/features/parkingManagement/components/AllowLists/table/CellAllowListMemberActions';
import { type AllowListMember } from 'src/features/parkingManagement/types/allowList';
import { allowListMessages } from 'src/features/parkingManagement/utils/allowListMessages';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { generalMessages } from 'src/utils/messages';

const columnHelper = createColumnHelper<AllowListMember>();

export default function AllowListMembersTable() {
    const { parkingId = '', listId = '' } = useParams();
    const { formatMessage } = useIntl();

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch();

    const { data, isLoading, isError, refetch } = useListAllowListMembers({
        ...queryPaginationOptions,
        ...querySearchOptions,
        parkingId,
        listId,
        config: { enabled: !!parkingId },
    });

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('identifier', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(allowListMessages.identifier),
            },
        }),
        columnHelper.accessor('email', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(allowListMessages.email),
            },
            cell: ({ getValue }) => getValue() || '-',
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellAllowListMemberActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable
            columns={columns}
            data={data}
            pagination={pagination}
            search={search}
            semantics={{
                title: formatMessage(allowListMessages.members),
                entryName: formatMessage(allowListMessages.member).toLowerCase(),
                entryNames: formatMessage(allowListMessages.members).toLowerCase(),
            }}
            isError={isError}
            isLoading={isLoading}
            refetch={refetch}
            components={{
                newEntry: CreateAllowListMemberFormModal,
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
            enableLocalFilters={true}
        />
    );
}
